import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type {
  DescriptionSyncJobFailure,
  DescriptionSyncJobStatus,
} from '../descriptionSyncJob'
import { isDescriptionSyncTerminal } from '../descriptionSyncJob'
import {
  fetchDescriptionSyncJobStatus,
  startDescriptionSyncJob,
} from '../descriptionSyncJobApi'
import { useAccountSettingsStore } from './accountSettings'

export const DESCRIPTION_SYNC_POLL_INTERVAL_MS = 5000

export const useDescriptionSyncJobStore = defineStore('descriptionSyncJob', () => {
  const accountSettings = useAccountSettingsStore()

  const jobAccountId = ref('')
  const jobId = ref('')
  const job = ref<DescriptionSyncJobStatus | null>(null)
  const starting = ref(false)
  const startFailure = ref<DescriptionSyncJobFailure | null>(null)
  const refreshFailure = ref<DescriptionSyncJobFailure | null>(null)

  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let polling = false
  let pollGeneration = 0
  let startGeneration = 0
  let startingAccountId = ''

  const hasJob = computed(() => jobId.value !== '')
  const isRunning = computed(
    () => hasJob.value && !isDescriptionSyncTerminal(job.value?.status),
  )

  const isCurrent = (accountId: string, id: string): boolean =>
    jobAccountId.value === accountId && jobId.value === id

  const stopPolling = () => {
    pollGeneration += 1
    polling = false
    if (pollTimer !== null) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  const reset = () => {
    stopPolling()
    startGeneration += 1
    jobAccountId.value = ''
    jobId.value = ''
    job.value = null
    starting.value = false
    startingAccountId = ''
    startFailure.value = null
    refreshFailure.value = null
  }

  const refresh = async (): Promise<void> => {
    const forAccountId = jobAccountId.value
    const forJobId = jobId.value
    if (!forAccountId || !forJobId) return

    const result = await fetchDescriptionSyncJobStatus(forAccountId, forJobId)
    if (!isCurrent(forAccountId, forJobId)) return

    if (!result.ok) {
      refreshFailure.value = result.failure
      return
    }

    refreshFailure.value = null
    job.value = result.data
  }

  const pollLoop = async (generation: number): Promise<void> => {
    await refresh()

    if (generation !== pollGeneration || !polling || !jobId.value) return
    if (isDescriptionSyncTerminal(job.value?.status)) {
      stopPolling()
      return
    }

    pollTimer = setTimeout(() => {
      void pollLoop(generation)
    }, DESCRIPTION_SYNC_POLL_INTERVAL_MS)
  }

  const startPolling = () => {
    if (polling || !jobId.value) return
    polling = true
    const generation = ++pollGeneration
    void pollLoop(generation)
  }

  const attachToJob = (accountId: string, id: string) => {
    if (isCurrent(accountId, id)) {
      startPolling()
      return
    }

    stopPolling()
    jobAccountId.value = accountId
    jobId.value = id
    job.value = null
    refreshFailure.value = null
    startPolling()
  }

  const start = async (): Promise<boolean> => {
    if (starting.value || isRunning.value) return false

    const accountId = accountSettings.accountId
    if (!accountId) return false

    const generation = ++startGeneration
    starting.value = true
    startingAccountId = accountId
    startFailure.value = null

    try {
      const result = await startDescriptionSyncJob(accountId)
      if (
        generation !== startGeneration ||
        accountSettings.accountId !== accountId
      ) {
        return false
      }

      if (result.ok) {
        attachToJob(accountId, result.data.jobId)
        return true
      }

      if (result.failure.kind === 'conflict' && result.failure.jobId) {
        attachToJob(accountId, result.failure.jobId)
        return true
      }

      startFailure.value = result.failure
      return false
    } finally {
      if (generation === startGeneration) {
        starting.value = false
        startingAccountId = ''
      }
    }
  }

  watch(
    () => accountSettings.accountId,
    currentAccountId => {
      if (
        (jobAccountId.value && currentAccountId !== jobAccountId.value) ||
        (starting.value && currentAccountId !== startingAccountId)
      ) {
        reset()
      }
    },
  )

  return {
    jobAccountId,
    jobId,
    job,
    starting,
    startFailure,
    refreshFailure,
    hasJob,
    isRunning,
    start,
    attachToJob,
    refresh,
    startPolling,
    stopPolling,
    reset,
  }
})
